import AuthLayout from "@/components/Auth/AuthLayout";
import {
  OnboardingProvider,
  useOnboardingContext,
} from "@/contexts/OnboardingContext";
import { SignUpProvider } from "@/contexts/SignUpContext";
import useGTM from "@/hooks/useGTM";
import { useStores } from "@/hooks/useStores";
import craft from "@/lib/craft";
import { useCroWeb180Experiment } from "@/src/experiments/cro-web-180/useCroWeb180Experiment";
import OnboardingContinueButton from "@/src/features/onboarding/components/OnboardingContinueButton";
import OnboardingExitModal from "@/src/features/onboarding/components/OnboardingExitModal";
import OnboardingHeader from "@/src/features/onboarding/components/OnboardingHeader";
import OnboardingLayout from "@/src/features/onboarding/components/OnboardingLayout";
import OnboardingStepContent from "@/src/features/onboarding/components/OnboardingStepContent";
import OnboardingStepImage from "@/src/features/onboarding/components/OnboardingStepImage";
import QueryUserOnboarding from "@queries/QueryUserOnboarding.graphql";
import { useRouter } from "next/router";
import posthog from "posthog-js";
import { useEffect, useMemo, useState } from "react";

// fallback CDN images
const fallbackStepImages: Record<string, string> = {
  welcome:
    "https://files.mob-cdn.co.uk/files/OnboardingFlowFiles/WelcomeStep.png",
  recipeSelection:
    "https://files.mob-cdn.co.uk/files/OnboardingFlowFiles/recipeSelectionBg.png",
  cookingHabits:
    "https://files.mob-cdn.co.uk/files/OnboardingFlowFiles/cookingHabitsStepBg.png",
};

export async function getStaticProps() {
  const queries = [{ document: QueryUserOnboarding }];
  const response = await craft(queries);

  const {
    userOnboarding,
    recipeSelectionStepAnswers,
    cookingHabitsStepAnswers,
    fallbackAnswer,
    seomatic,
  } = response[0];

  return {
    props: {
      userOnboarding,
      recipeSelectionStepAnswers,
      cookingHabitsStepAnswers,
      fallbackAnswer,
      seomatic,
    },
    revalidate: process.env.VERCEL_ENV !== "production" ? 1 : 3600,
  };
}

const OnboardingPage = ({
  userOnboarding,
  recipeSelectionStepAnswers,
  cookingHabitsStepAnswers,
}) => {
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const {
    currentStepIndex,
    setCurrentStepIndex,
    onboardingSteps,
    removeDynamicStep,
  } = useOnboardingContext();

  // CRO-WEB-180 experiment - redirect to /start if user is in the "start" variant
  useCroWeb180Experiment();

  let currentStep = onboardingSteps[currentStepIndex];
  if (!currentStep) {
    const prevStep = onboardingSteps[currentStepIndex - 1];

    if (prevStep) {
      currentStep = prevStep;
    }
  }

  const currentStepKey = currentStep?.key;

  // Dynamic button label based on current step
  const buttonLabel = useMemo(() => {
    switch (currentStepKey) {
      case "welcome":
        return "Get started for free";
      default:
        return "Continue";
    }
  }, [currentStepKey]);
  const currentStepData = userOnboarding.onboardingSteps.find(
    (step) => step.stepKey === currentStepKey
  );

  const logoUrl = currentStepData?.logo?.[0]?.url || "";
  const logoAlt = currentStepData?.logo?.[0]?.title || "";
  const router = useRouter();
  const gtm = useGTM();
  const { authStore } = useStores();
  const currentUser = authStore.currentUser;

  useEffect(() => {
    if (currentStepIndex === onboardingSteps.length) {
      posthog.capture("onboarding_stage_1_completed");

      router.push("/sign-up");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex, onboardingSteps.length]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const onboardingCompleted = sessionStorage.getItem("onboardingCompleted");

      if (onboardingCompleted) {
        setCurrentStepIndex(onboardingSteps.length - 1);
      }

      sessionStorage.removeItem("onboardingCompleted");
    }
  }, [onboardingSteps.length, setCurrentStepIndex]);

  // Calculate progress index based on a clean sequential mapping
  const currentVisibleIndex = useMemo(() => {
    // Handle dynamic follow-up step keys that include answer IDs
    if (currentStepKey === "welcome") return -1; // No progress bar shown
    if (currentStepKey === "recipeSelection") return 0; // 12.5% (1/8)
    if (currentStepKey?.startsWith("recipeSelectionFollowUp")) return 1; // 25% (2/8)
    if (currentStepKey === "cookingHabits") return 2; // 37.5% (3/8)
    if (currentStepKey?.startsWith("cookingHabitsFollowUp")) return 3; // 50% (4/8)

    return 0; // fallback
  }, [currentStepKey]);

  // Use total that reflects the complete flow (4 onboarding steps + 4 signup steps = 8 total)
  const totalSteps = 8;

  if (!currentStep) {
    return null;
  }

  const imageUrl =
    currentStep.image?.url ||
    currentStepData?.image?.[0]?.url ||
    currentStepData?.linkedOptionImages?.[0]?.url ||
    fallbackStepImages[currentStepKey] ||
    "";

  const handleFireEvent = (
    stepKey: string,
    stepAnswer: { title?: string } | null
  ) => {
    switch (stepKey) {
      case "welcome":
        gtm.pushEvent("onboarding_started");
        posthog.capture("onboarding_welcome_cta_clicked");
        break;

      case "recipeSelection":
        gtm.pushEvent("onboarding_step_completed", {
          onboardingStepName: stepKey,
          onboardingWhatsImportant: stepAnswer?.title,
        });
        break;
      case "cookingHabits":
        gtm.pushEvent("onboarding_step_completed", {
          onboardingStepName: stepKey,
          onboardingHowYouCook: stepAnswer?.title,
        });
        break;
    }
  };

  const handleContinue = (answer) => {
    handleFireEvent(currentStepKey, answer);

    const nextStep = currentStepIndex + 1;

    setCurrentStepIndex(nextStep);
  };

  const handleBack = () => {
    // if current step is a dynamic step, remove it from the onboardingSteps
    if (currentStep.isFollowUpStep) {
      removeDynamicStep(currentStep.key);
    }

    const prevStep = currentStepIndex - 1;
    setCurrentStepIndex(prevStep);
  };

  const handleClose = () => {
    setIsCloseModalOpen(true);
  };

  const handleRequestClose = () => {
    router.push("/");

    gtm.pushEvent("onboarding_exited", {
      onboardingStepName: currentStepKey,
    });
  };

  const stepProps = {
    handleContinue,
    stepData: currentStepData,
    recipeSelectionStepAnswers,
    cookingHabitsStepAnswers,
  } as Record<string, unknown>;

  const showContinueButton =
    !!currentStepKey &&
    !["recipeSelection", "cookingHabits"].includes(currentStepKey);

  return (
    <>
      <OnboardingExitModal
        isOpen={isCloseModalOpen}
        onClose={() => setIsCloseModalOpen(false)}
        onConfirmExit={handleRequestClose}
      />

      <OnboardingLayout
        imageSection={
          <OnboardingStepImage
            currentStepKey={currentStepKey}
            imageUrl={imageUrl}
            currentStep={currentStep}
          />
        }
      >
        <OnboardingHeader
          currentStepKey={currentStepKey}
          currentStepIndex={currentStepIndex}
          currentVisibleIndex={currentVisibleIndex}
          totalSteps={totalSteps}
          logoUrl={logoUrl}
          logoAlt={logoAlt}
          onBack={handleBack}
          onClose={handleClose}
        />

        <OnboardingStepContent
          currentStep={currentStep}
          stepProps={stepProps}
        />

        <OnboardingContinueButton
          currentStepKey={currentStepKey}
          currentStepIndex={currentStepIndex}
          onboardingStepsLength={onboardingSteps.length}
          buttonLabel={buttonLabel}
          onContinue={() => handleContinue(null)}
          currentUser={currentUser}
          showButton={showContinueButton}
        />
      </OnboardingLayout>
    </>
  );
};

const WrappedOnboardingPage = (props) => {
  return (
    <OnboardingProvider>
      <SignUpProvider>
        <OnboardingPage {...props} />
      </SignUpProvider>
    </OnboardingProvider>
  );
};

WrappedOnboardingPage.getLayout = (page) => {
  const { props } = page;
  const { notifications, navigation, colourway } = props;

  return (
    <AuthLayout
      notifications={notifications}
      colourway={colourway}
      navigation={navigation}
      promoBanner={null}
      primaryNavigation={null}
      breadcrumbs={null}
      fullWidth
    >
      {page}
    </AuthLayout>
  );
};

export default WrappedOnboardingPage;
